<template>
  <RevCard class="p-24">
    <div class="flex justify-between flex-col gap-24 h-full">
      <div class="space-y-24">
        <div class="space-y-8">
          <RevLink
            class="body-1-bold"
            :tracking="{
              zone: 'telco_comparison_cards',
              name: 'partner_name_redirect',
              value: trackingValue,
            }"
            :to="fullDetailsLink"
            :underlined="false"
          >
            <span class="flex items-center gap-8">
              <slot name="logo" />

              {{ name }}
            </span>
          </RevLink>

          <div class="flex items-center gap-4">
            <RevRating :score="trustpilotRating" size="extra-small" />

            <span class="text-static-default-low caption">
              {{ i18n(translations.onTrustpilot) }}
            </span>
          </div>
        </div>

        <div>
          <p class="text-static-default-low caption">
            {{ i18n(translations.startingAt) }}
          </p>

          <p class="heading-2 space-x-4">
            <span>{{ price }}</span>

            <span v-if="formerPrice" class="line-through">
              {{ formerPrice }}
            </span>

            <span>{{ i18n(translations.priceSuffix) }}</span>

            <span v-if="taxes" class="body-2-bold">
              {{ i18n(translations.taxes) }}
            </span>
          </p>
        </div>

        <div class="body-1-bold">
          <slot name="info" />
        </div>

        <div class="space-y-8">
          <ul class="space-y-12">
            <li
              v-for="benefit in benefits"
              :key="benefit"
              class="flex items-start gap-8"
            >
              <IconCheckSmall
                class="-top-2 relative text-static-brand-mid mt-2 shrink-0"
                size="20"
              />

              <span class="body-1">{{ benefit }}</span>
            </li>
          </ul>

          <RevLink
            class="body-2-link"
            :tracking="{
              zone: 'telco_comparison_cards',
              name: 'full_details_redirect',
              value: trackingValue,
            }"
            :to="fullDetailsLink"
          >
            {{ i18n(translations.seeFullDetails) }}
          </RevLink>
        </div>
      </div>

      <RevDialog :name="dialogName" :title="i18n(translations.modalTitle)">
        <template #trigger="{ open }">
          <RevButton
            class="mt-16"
            full-width="always"
            :tracking="{
              zone: 'telco_comparison_cards',
              name: 'get_started_cta',
              value: trackingValue,
            }"
            variant="primary"
            @click="open"
          >
            {{ i18n(translations.getStarted) }}
          </RevButton>
        </template>

        <template #body>
          <slot name="modal" />
        </template>

        <template #footer="{ close }">
          <RevButton
            full-width="always"
            :tracking="{
              zone: 'telco_instructional_popup',
              name: 'close',
              value: trackingValue,
            }"
            variant="secondary"
            @click="close"
          >
            {{ i18n(translations.close) }}
          </RevButton>
        </template>
      </RevDialog>
    </div>
  </RevCard>
</template>

<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDialog } from '@ds/components/Dialog'
import { RevLink } from '@ds/components/Link'
import { RevRating } from '@ds/components/Rating'
import { IconCheckSmall } from '@ds/icons/IconCheckSmall'

import translations from './OfferComparisonCard.translations'

defineProps<{
  dialogName: string
  fullDetailsLink: RouteLocationRaw
  name: string
  trustpilotRating: number
  price: string
  formerPrice?: string
  benefits: string[]
  taxes?: boolean
  trackingValue: string
}>()

defineSlots<{
  logo?: () => unknown
  info?: () => unknown
  modal?: () => unknown
}>()

const i18n = useI18n()
</script>
